import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import {
  createVenue,
  getVenueById,
  updateVenue,
  uploadVenueImages,
  uploadVenueVideo,
} from '../firebase/venueService';
import { searchFoursquarePlaces } from '../firebase/foursquareService';
import VenueCardPreview from '../components/VenueCardPreview';
import FeatureLocked from '../components/FeatureLocked';
import {
  BrandInput,
  BrandLabel,
  BrandSelect,
  BrandSpinner,
  BrandTextarea,
  PageShell,
  PANEL,
  RING_SETS,
  SegmentedRule,
  WELL,
  brandButton,
} from '../components/ui/Brand';
import { useError } from '../context/ErrorContext';
import { useSubscription } from '../hooks/useSubscription';

const stepTitleClass =
  'font-display text-2xl font-bold tracking-tight text-white sm:text-3xl';

const EMPTY_HOURS = {
  monday: { open: '', close: '', closed: false },
  tuesday: { open: '', close: '', closed: false },
  wednesday: { open: '', close: '', closed: false },
  thursday: { open: '', close: '', closed: false },
  friday: { open: '', close: '', closed: false },
  saturday: { open: '', close: '', closed: false },
  sunday: { open: '', close: '', closed: false },
};

const EMPTY_VENUE = {
  placeId: '', // Foursquare fsq_place_id — set once ownership is confirmed
  // Captured from the same Foursquare result as placeId. null (not 0) when
  // unknown — the consumer app tests `typeof === 'number'`, so a 0 would read
  // as the Gulf of Guinea rather than as "no pin".
  latitude: null,
  longitude: null,
  title: '',
  address: '',
  phone: '',
  website: '',
  categories: [], // Up to MAX_CATEGORIES; first one is the primary
  description: '',
  cardBorderStyle: 'default',
  socialLinks: {
    instagram: '',
    facebook: '',
    tiktok: '',
  },
  hours: EMPTY_HOURS,
  // Consumer-app discovery filters. All optional — the app is lenient toward
  // missing values (a venue with no value is kept, not excluded), so a blank
  // field costs the owner reach rather than visibility. Vocabularies must
  // match the app's exactly; see the option lists further down.
  dressCode: '',
  coverCharge: '',
  musicGenres: [],
  dietaryOptions: [],
  offers: [],
};

// Maps a persisted venue doc back onto the wizard's form shape. The
// consumer app stores the primary category singular (`category`) and the
// full set (`categories`); read whichever is populated.
const venueToForm = (v) => ({
  placeId: v.placeId || '',
  latitude: typeof v.latitude === 'number' ? v.latitude : null,
  longitude: typeof v.longitude === 'number' ? v.longitude : null,
  title: v.name || v.title || '',
  address: v.address || '',
  phone: v.phone || '',
  website: v.website || '',
  categories:
    v.categories && v.categories.length > 0
      ? v.categories
      : v.category
        ? [v.category]
        : [],
  description: v.description || '',
  cardBorderStyle: v.cardBorderStyle || 'default',
  socialLinks: {
    instagram: '',
    facebook: '',
    tiktok: '',
    ...(v.socialLinks || {}),
  },
  hours: v.hours && Object.keys(v.hours).length > 0 ? v.hours : EMPTY_HOURS,
  dressCode: v.dressCode || '',
  // Kept as a string in form state so the input can be genuinely empty;
  // 0 is a meaningful value here (free entry), not a blank.
  coverCharge: typeof v.coverCharge === 'number' ? String(v.coverCharge) : '',
  musicGenres: v.musicGenres || [],
  dietaryOptions: v.dietaryOptions || [],
  offers: v.offers || [],
});

function CreateVenue() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess } = useError();
  // Editing an existing card (or resuming a draft) when the route carries
  // an :id. `venueId` is the doc we read from and write back to; it also
  // gets set mid-create the first time a draft is saved, so every later
  // save updates that same doc instead of spawning duplicates.
  const params = useParams();
  const [venueId, setVenueId] = useState(params.id || null);
  const isEdit = Boolean(venueId);

  // Tier comes from the owner's existing venue (first-time creators
  // have none → trial → premium styling stays locked).
  const { activeVenue } = useOutletContext() || {};
  const {
    customBorders,
    offers: offersUnlocked,
    videoUploads,
  } = useSubscription(activeVenue && activeVenue.subscriptionTier);

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loadingVenue, setLoadingVenue] = useState(Boolean(params.id));

  // The card's images, in display order. Existing images carry a Cloudinary
  // `url` only; newly-picked ones also carry the `file` awaiting upload. A
  // save uploads just the files, keeps the urls, and preserves order.
  const [images, setImages] = useState([]);

  // Same shape as one image slot: an existing clip is `{ url }`, a freshly
  // picked one is `{ url, file }` where the url is a local object URL used
  // only for the inline player until a save uploads the file.
  const [video, setVideo] = useState(null);

  const [venueData, setVenueData] = useState(EMPTY_VENUE);

  // Prefill from the existing doc when editing / resuming a draft. This is
  // what makes "Save Draft → come back later" actually restore the card
  // (previously the edit route rendered a blank wizard).
  useEffect(() => {
    if (!params.id) return;
    let alive = true;
    (async () => {
      try {
        const venue = await getVenueById(params.id);
        if (!alive) return;
        if (venue) {
          setVenueData(venueToForm(venue));
          setImages((venue.images || []).map((url) => ({ url })));
          setVideo(venue.video ? { url: venue.video } : null);
        } else {
          showError('That card no longer exists — starting a new one.');
          setVenueId(null);
        }
      } catch (error) {
        showError('Failed to load this card for editing.');
      } finally {
        if (alive) setLoadingVenue(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [params.id, showError]);

  const updateVenueData = (field, value) => {
    setVenueData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Uploads any newly-picked files and returns the final ordered url list,
  // interleaving freshly-uploaded urls back into their original slots.
  const resolveImageUrls = useCallback(
    async (targetId) => {
      const newFiles = images.filter((i) => i.file).map((i) => i.file);
      const uploaded = newFiles.length
        ? await uploadVenueImages(newFiles, targetId)
        : [];
      let next = 0;
      return images.map((i) => (i.file ? uploaded[next++] : i.url));
    },
    [images]
  );

  // The single save path for both "Save Draft" and "Launch Venue". A draft
  // is just an unpublished venue, so both write the same doc — `strict`
  // only decides how much we validate first and where we go afterwards.
  const persistVenue = async ({ strict }) => {
    if (strict) {
      if (!venueData.placeId) {
        showError('Confirm your venue on Foursquare in Step 1 to launch.');
        setCurrentStep(1);
        return;
      }
      if (
        !venueData.title ||
        !venueData.address ||
        venueData.categories.length === 0 ||
        !venueData.phone
      ) {
        showError(
          'Please fill in Title, Address, Phone, and at least one Category.'
        );
        return;
      }
      if (images.length === 0) {
        showError('Please upload at least one image.');
        return;
      }
    } else if (!venueData.title.trim()) {
      showError('Add a venue name before saving a draft.');
      return;
    }

    setSaving(true);
    try {
      const corePayload = {
        placeId: venueData.placeId,
        name: venueData.title,
        // First selection doubles as the primary category (the consumer
        // app still reads the singular field).
        category: venueData.categories[0] || '',
        categories: venueData.categories,
        address: venueData.address,
        phone: venueData.phone,
        website: venueData.website,
        description: venueData.description,
        cardBorderStyle: venueData.cardBorderStyle,
        socialLinks: venueData.socialLinks,
        hours: venueData.hours,
        dressCode: venueData.dressCode,
        musicGenres: venueData.musicGenres,
        dietaryOptions: venueData.dietaryOptions,
        offers: venueData.offers,
        // Written only when real. The consumer app's coordinate resolver and
        // its cover-charge filter both test `typeof === 'number'`, so writing
        // null would read as "no value" anyway — but it would also overwrite a
        // good pin from a previous save with an empty one.
        ...(typeof venueData.latitude === 'number' &&
        typeof venueData.longitude === 'number'
          ? { latitude: venueData.latitude, longitude: venueData.longitude }
          : {}),
        ...(venueData.coverCharge !== '' &&
        Number.isFinite(Number(venueData.coverCharge))
          ? { coverCharge: Number(venueData.coverCharge) }
          : {}),
      };

      // Reuse the existing doc when we have one (edit or a resaved draft),
      // otherwise create it — a new draft creates an unpublished venue.
      let targetId = venueId;
      if (targetId) {
        await updateVenue(targetId, corePayload);
      } else {
        targetId = await createVenue(
          { ...corePayload, images: [] },
          currentUser.uid
        );
        setVenueId(targetId);
      }

      const finalImages = await resolveImageUrls(targetId);
      // Only a freshly-picked clip needs uploading; an existing one already
      // carries its Cloudinary url and must survive an edit untouched.
      const finalVideo = video?.file
        ? await uploadVenueVideo(video.file, targetId)
        : video?.url || null;
      await updateVenue(targetId, { images: finalImages, video: finalVideo });
      // Reflect the now-uploaded urls locally so a later save in the same
      // session doesn't re-upload the same files.
      setImages(finalImages.map((url) => ({ url })));
      setVideo(finalVideo ? { url: finalVideo } : null);

      if (strict) {
        showSuccess(
          isEdit ? 'Venue updated successfully!' : 'Venue created successfully!'
        );
        navigate('/dashboard');
      } else {
        showSuccess('Draft saved.');
        // Put the new id in the URL so this becomes a resumable edit and
        // subsequent saves target the same doc.
        if (!venueId) navigate(`/venue/edit/${targetId}`, { replace: true });
      }
    } catch (error) {
      showError(
        strict
          ? `Failed to save venue: ${error.message}`
          : 'Failed to save draft. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const goToNextStep = () => {
    if (currentStep === 1 && !venueData.placeId) {
      showError(
        'Find and select your venue on Foursquare to confirm ownership.'
      );
      return;
    }
    if (currentStep < 4) setCurrentStep((prev) => prev + 1);
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  if (loadingVenue) {
    return (
      <div className="flex min-h-screen flex-1 flex-col items-center justify-center gap-4 bg-brand-ink p-12">
        <BrandSpinner />
        <p className="font-mono text-sm text-white/70">Loading your card…</p>
      </div>
    );
  }

  // Preview reads image src strings off venueData.images; feed it the live
  // display urls without keeping images inside the form state.
  const previewData = {
    ...venueData,
    images: images.map((i) => i.url),
    video: video?.url || null,
  };

  return (
    <PageShell rings={RING_SETS.split} width="max-w-7xl">
      <div className="flex gap-8 max-lg:flex-col">
        <div className="flex w-1/2 items-center justify-center max-lg:w-full">
          <VenueCardPreview venueData={previewData} currentStep={currentStep} />
        </div>

        <div
          className={`${PANEL} flex w-1/2 flex-col gap-6 p-8 max-lg:w-full max-md:p-6`}
        >
          {/* Focus Mode Escape Hatch */}
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 font-mono text-sm text-white/60 transition-colors hover:text-white"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              Back to Dashboard
            </button>
            <span className="font-display text-sm font-bold uppercase tracking-wider text-white">
              Venue Setup
            </span>
          </div>

          <SegmentedRule variant="warm" />

          <div className="flex items-center justify-between gap-6">
            <div className="flex flex-1 gap-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-1.5 flex-1 rounded-full transition ${
                    currentStep >= step ? 'bg-brand-warm' : 'bg-white/15'
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              className={brandButton('outline', 'sm', 'shrink-0')}
              onClick={() => persistVenue({ strict: false })}
              disabled={saving}
            >
              Save Draft
            </button>
          </div>

          {currentStep === 1 && (
            <Step1Identity
              venueData={venueData}
              updateVenueData={updateVenueData}
            />
          )}
          {currentStep === 2 && (
            <Step2Category
              venueData={venueData}
              updateVenueData={updateVenueData}
            />
          )}
          {currentStep === 3 && (
            <Step3Images
              images={images}
              setImages={setImages}
              video={video}
              setVideo={setVideo}
              videoUnlocked={videoUploads}
            />
          )}
          {currentStep === 4 && (
            <Step4Operations
              venueData={venueData}
              updateVenueData={updateVenueData}
              customBorders={customBorders}
              offersUnlocked={offersUnlocked}
            />
          )}

          <div className="mt-auto flex items-center justify-end gap-4 pt-2">
            {currentStep > 1 && (
              <button
                type="button"
                className={brandButton('outline', 'lg', 'mr-auto')}
                onClick={goToPreviousStep}
              >
                Back
              </button>
            )}
            {currentStep < 4 ? (
              <button
                type="button"
                className={brandButton('primary', 'lg')}
                onClick={goToNextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className={brandButton('primary', 'lg')}
                onClick={() => persistVenue({ strict: true })}
                disabled={saving}
              >
                {saving
                  ? isEdit
                    ? 'Saving…'
                    : 'Creating Profile...'
                  : isEdit
                    ? 'Save Changes'
                    : 'Launch Venue'}
              </button>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// --- STEPS COMPONENTS ---

function Step1Identity({ venueData, updateVenueData }) {
  const [query, setQuery] = useState(venueData.title || '');
  const [area, setArea] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const runSearch = async () => {
    if (!query.trim()) {
      setSearchError('Enter your venue name to search.');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const found = await searchFoursquarePlaces({
        query: query.trim(),
        near: area.trim(),
      });
      setResults(found);
      if (found.length === 0) {
        setSearchError('No matches. Try adding the suburb or city.');
      }
    } catch (err) {
      setSearchError('Place search is unavailable right now. Try again.');
    } finally {
      setSearching(false);
    }
  };

  const selectPlace = (place) => {
    updateVenueData('placeId', place.placeId);
    updateVenueData('title', place.name);
    if (place.address) updateVenueData('address', place.address);
    // Coordinates ride along on the same result the placeId came from — the
    // proxy already normalizes Foursquare's two geometry paths server-side
    // (functions/index.js), so there is nothing to unpick here. Without them
    // the consumer app falls back to a deterministic dummy pin ~3 km around
    // the JHB CBD, which is what its distance filter would then be filtering
    // on. Stored only when both are real numbers; a partial pair is no pin.
    if (
      typeof place.latitude === 'number' &&
      typeof place.longitude === 'number'
    ) {
      updateVenueData('latitude', place.latitude);
      updateVenueData('longitude', place.longitude);
    }
    setResults([]);
  };

  // "Change" must drop the coordinates too — a pin left over from the
  // previously confirmed place would silently attach to a different venue.
  const clearPlace = () => {
    updateVenueData('placeId', '');
    updateVenueData('latitude', null);
    updateVenueData('longitude', null);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className={stepTitleClass}>Venue Identity &amp; Contact</h1>

      {venueData.placeId ? (
        <div
          className={`${WELL} flex items-start justify-between gap-3 border-brand-green/40`}
        >
          <div className="flex items-start gap-2">
            <CheckBadgeIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-brand-green"
              aria-hidden="true"
            />
            <div>
              <p className="font-mono text-sm font-bold text-white">
                Ownership confirmed on Foursquare
              </p>
              <p className="font-mono text-sm text-white/60">
                {venueData.title}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="font-mono text-sm font-bold text-brand-orange hover:underline"
            onClick={clearPlace}
          >
            Change
          </button>
        </div>
      ) : (
        <div className={`${WELL} flex flex-col gap-3`}>
          <div>
            <p className="font-mono text-sm font-bold text-white">
              Find your venue to confirm ownership
            </p>
            <p className="font-mono text-xs text-white/55">
              We match your Foursquare listing so your card links to the
              consumer app. Required to launch.
            </p>
          </div>
          <BrandInput
            type="text"
            placeholder="Search by name (e.g. The Living Room)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <BrandInput
            type="text"
            placeholder="Suburb / city (e.g. Maboneng, Johannesburg)"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
          <button
            type="button"
            className={brandButton('outline', 'sm', 'self-start')}
            onClick={runSearch}
            disabled={searching}
          >
            {searching ? 'Searching…' : 'Search Foursquare'}
          </button>

          {searchError && (
            <p className="font-mono text-sm text-brand-pink">{searchError}</p>
          )}

          {results.length > 0 && (
            <ul className="flex flex-col gap-2">
              {results.map((place) => (
                <li key={place.placeId}>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-white/15 bg-white/[0.06] px-4 py-3 text-left transition hover:border-brand-orange"
                    onClick={() => selectPlace(place)}
                  >
                    <p className="font-mono text-sm font-bold text-white">
                      {place.name}
                    </p>
                    {place.address && (
                      <p className="font-mono text-xs text-white/55">
                        {place.address}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <BrandInput
        type="text"
        placeholder="Venue Name"
        value={venueData.title}
        onChange={(e) => updateVenueData('title', e.target.value)}
      />
      <BrandInput
        type="text"
        placeholder="Full Address"
        value={venueData.address}
        onChange={(e) => updateVenueData('address', e.target.value)}
      />
      <BrandInput
        type="tel"
        placeholder="Public Phone Number"
        value={venueData.phone}
        onChange={(e) => updateVenueData('phone', e.target.value)}
      />
      <BrandInput
        type="url"
        placeholder="Website URL"
        value={venueData.website}
        onChange={(e) => updateVenueData('website', e.target.value)}
      />
    </div>
  );
}

// THE canonical venue vocabulary, shared with the consumer app's
// VENUE_CATEGORIES and with CATEGORY_ALIASES in
// functions/refreshDistrictVenues.js. All three must agree: the app's genre
// filter excludes any venue whose tags don't overlap the user's selection, so
// a value one side can write and another can't select hides that venue from
// every search rather than merely mislabelling it.
const VENUE_CATEGORIES = [
  'bar',
  'club',
  'lounge',
  'pub',
  'cocktail bar',
  'wine bar',
  'rooftop',
  'live music',
  'restaurant',
  'sports bar',
  // Triggers the FPB X18 compliance flow in Settings → Legal & Compliance.
  // Deliberately has no CATEGORY_ALIASES entry: auto-created stubs bypass that
  // acceptance flow, so X18 venues reach the deck only by being claimed here.
  'adult entertainment',
];

const MAX_CATEGORIES = 3;

function Step2Category({ venueData, updateVenueData }) {
  const selected = venueData.categories;
  const atLimit = selected.length >= MAX_CATEGORIES;

  const toggleCategory = (cat) => {
    if (selected.includes(cat)) {
      updateVenueData(
        'categories',
        selected.filter((c) => c !== cat)
      );
    } else if (!atLimit) {
      updateVenueData('categories', [...selected, cat]);
    }
  };

  return (
    <div className="flex flex-col">
      <h1 className={stepTitleClass}>Venue Categories</h1>
      <p className="mb-4 mt-2 font-mono text-sm text-white/55">
        Select up to {MAX_CATEGORIES} categories — the first pick is your
        primary ({selected.length}/{MAX_CATEGORIES} selected)
      </p>
      <div className="grid grid-cols-3 gap-3 max-md:grid-cols-2">
        {VENUE_CATEGORIES.map((cat) => {
          const isSelected = selected.includes(cat);
          return (
            <button
              key={cat}
              type="button"
              data-testid={`category-${cat.replace(/\s+/g, '-')}`}
              disabled={!isSelected && atLimit}
              className={`rounded-lg border px-4 py-3 font-mono text-sm capitalize transition disabled:cursor-not-allowed disabled:opacity-40 ${
                isSelected
                  ? 'border-transparent bg-brand-warm font-bold text-white'
                  : 'border-white/20 bg-white/[0.06] text-white/70 hover:border-white/45 hover:text-white'
              }`}
              onClick={() => toggleCategory(cat)}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Renders up to four image slots off the unified `images` model (each item
// is an existing `{ url }` or a freshly-picked `{ url, file }`). New picks
// get an object URL just for the thumbnail; the File rides along until a
// save uploads it. Existing images already carry their Cloudinary url, so
// editing a card no longer starts from an empty gallery.
function Step3Images({ images, setImages, video, setVideo, videoUnlocked }) {
  const [uploadError, setUploadError] = useState('');

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadError('');
    const remainingSlots = 4 - images.length;
    const filesToAdd = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      setUploadError(
        `Only ${remainingSlots} more image(s) can be added (max 4).`
      );
    }

    setImages([
      ...images,
      ...filesToAdd.map((file) => ({ url: URL.createObjectURL(file), file })),
    ]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col">
      <h1 className={`${stepTitleClass} mb-4`}>High-Resolution Media</h1>
      {uploadError && (
        <div
          className={`${WELL} mb-4 flex items-start gap-2 border-brand-pink/50 font-mono text-sm text-brand-pink`}
        >
          <ExclamationTriangleIcon
            className="mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          {uploadError}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        {[0, 1, 2, 3].map((index) => (
          <div key={`image-${index}`} className="aspect-square">
            {images[index] ? (
              <div className="relative h-full w-full overflow-hidden rounded-xl border border-white/15">
                <img
                  src={images[index].url}
                  alt={`Upload ${index}`}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-brand-pink"
                  onClick={() => removeImage(index)}
                  aria-label="Remove image"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <label
                className="flex h-full w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/25 bg-white/[0.04] transition hover:border-brand-orange hover:bg-white/[0.08]"
                htmlFor={`image-upload-${index}`}
              >
                <PlusIcon className="h-8 w-8 text-white/40" aria-hidden="true" />
                <input
                  id={`image-upload-${index}`}
                  type="file"
                  accept="image/*"
                  multiple={index === 0}
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <BrandLabel>Venue Video (7s loop):</BrandLabel>
        {videoUnlocked ? (
          <VideoUploader video={video} setVideo={setVideo} />
        ) : (
          <FeatureLocked
            variant="compact"
            requiredTier="pro"
            featureName="Venue Video"
          >
            <VideoUploader video={video} setVideo={() => {}} />
          </FeatureLocked>
        )}
      </div>
    </div>
  );
}

// Cloudinary caps the free tier well above this, but a swipe card that has to
// buffer is worse than one with no clip at all — so the limit is about the
// consumer's first impression, not about storage.
const MAX_VIDEO_MB = 15;

function VideoUploader({ video, setVideo }) {
  const [error, setError] = useState('');

  const handlePick = (e) => {
    const file = e.target.files[0];
    setError('');
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setError(`Keep it under ${MAX_VIDEO_MB}MB so the card loads instantly.`);
      return;
    }
    setVideo({ url: URL.createObjectURL(file), file });
  };

  if (video) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-white/15">
        <video
          src={video.url}
          data-testid="venue-video-preview"
          className="h-40 w-full bg-black object-cover"
          muted
          loop
          playsInline
          controls
        />
        <button
          type="button"
          onClick={() => setVideo(null)}
          aria-label="Remove video"
          data-testid="venue-video-remove"
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-brand-pink"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <>
      {error && (
        <p className="font-mono text-sm text-brand-pink" role="alert">
          {error}
        </p>
      )}
      <label
        htmlFor="video-upload"
        className="flex h-24 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/25 bg-white/[0.04] font-mono text-sm text-white/55 transition hover:border-brand-orange hover:bg-white/[0.08]"
      >
        <PlusIcon className="h-6 w-6 text-white/40" aria-hidden="true" />
        Add a short clip
        <input
          id="video-upload"
          type="file"
          accept="video/*"
          data-testid="venue-video-input"
          onChange={handlePick}
          className="hidden"
        />
      </label>
    </>
  );
}

// Premium swipe-card border options (Pro+). Keys persist to
// venue.cardBorderStyle and drive VenueCardPreview's live shell.
// Never rename the keys — they are stored in Firestore documents.
// ── Consumer-app discovery vocabularies ──────────────────────────────────────
// These MUST match the consumer app's lists character for character. Its
// filters lowercase both sides for genres and dietary, but compare dressCode
// with strict equality — a capitalised or space-separated value there would
// silently never match, hiding the venue from that filter rather than erroring.
// Mirrors DRESS_CODE_OPTIONS / DIETARY_OPTIONS / FILTER_GENRE_OPTIONS in
// BarHop-MobileApp/src/types.ts.

const DRESS_CODES = [
  { value: 'casual', label: 'Casual' },
  { value: 'smart-casual', label: 'Smart Casual' },
  { value: 'formal', label: 'Formal' },
];

// Folded into the SAME filter dimension as categories on the consumer side —
// the app unions categories + category + musicGenres into one tag list — so
// these widen a venue's reach rather than adding a separate axis.
const MUSIC_GENRES = ['amapiano', 'techno', 'house', 'rnb', 'hip-hop', 'jazz'];

const DIETARY_OPTIONS = [
  'halal',
  'vegetarian',
  'vegan',
  'gluten-free',
  'kosher',
  'pescatarian',
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

const MAX_OFFER_TITLE = 40;

// Uncapped multi-select chip row, styled to match the category grid in step 2.
function ChipMultiSelect({ options, selected, onChange, testIdPrefix }) {
  const toggle = (value) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option);
        return (
          <button
            key={option}
            type="button"
            data-testid={`${testIdPrefix}-${option}`}
            aria-pressed={isSelected}
            onClick={() => toggle(option)}
            className={`rounded-full border px-4 py-2 font-mono text-sm capitalize transition ${
              isSelected
                ? 'border-transparent bg-brand-warm font-bold text-white'
                : 'border-white/20 bg-white/[0.06] text-white/70 hover:border-white/45 hover:text-white'
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

// Time-boxed promos rendered on the swipe card. The consumer app filters them
// client-side on `active && (validUntil ?? ∞) > now && daysOfWeek ∋ today`, so
// an expired offer never needs a scheduled function to retire it — but it does
// need the owner to be able to set the window, which is what this editor is.
function OffersEditor({ offers, onChange }) {
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    validUntil: '',
    daysOfWeek: [],
  });

  const addOffer = () => {
    const title = draft.title.trim();
    if (!title) return;
    onChange([
      ...offers,
      {
        // Stable client id — the app keys its offer list on this, and a
        // Firestore array has no document ids of its own.
        id: `offer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: title.slice(0, MAX_OFFER_TITLE),
        ...(draft.description.trim()
          ? { description: draft.description.trim() }
          : {}),
        ...(draft.validUntil
          ? { validUntil: new Date(`${draft.validUntil}T23:59:59`) }
          : {}),
        ...(draft.daysOfWeek.length ? { daysOfWeek: draft.daysOfWeek } : {}),
        active: true,
      },
    ]);
    setDraft({ title: '', description: '', validUntil: '', daysOfWeek: [] });
  };

  const toggleDay = (day) =>
    setDraft((d) => ({
      ...d,
      daysOfWeek: d.daysOfWeek.includes(day)
        ? d.daysOfWeek.filter((x) => x !== day)
        : [...d.daysOfWeek, day],
    }));

  const removeOffer = (id) => onChange(offers.filter((o) => o.id !== id));

  const toggleActive = (id) =>
    onChange(
      offers.map((o) => (o.id === id ? { ...o, active: !o.active } : o))
    );

  return (
    <div className="flex flex-col gap-4">
      {offers.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="offers-list">
          {offers.map((offer) => (
            <li
              key={offer.id}
              data-testid={`offer-${offer.id}`}
              className={`${WELL} flex items-start justify-between gap-3`}
            >
              <div className="min-w-0">
                <p
                  className={`font-mono text-sm font-bold ${
                    offer.active === false
                      ? 'text-white/40 line-through'
                      : 'text-white'
                  }`}
                >
                  {offer.title}
                </p>
                {offer.description && (
                  <p className="font-mono text-xs text-white/55">
                    {offer.description}
                  </p>
                )}
                {offer.daysOfWeek && offer.daysOfWeek.length > 0 && (
                  <p className="mt-1 font-mono text-xs text-white/45">
                    {DAYS_OF_WEEK.filter((d) =>
                      offer.daysOfWeek.includes(d.value)
                    )
                      .map((d) => d.label)
                      .join(', ')}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => toggleActive(offer.id)}
                  data-testid={`offer-toggle-${offer.id}`}
                  className="rounded px-2 py-1 font-mono text-xs text-white/55 transition hover:bg-white/10 hover:text-white"
                >
                  {offer.active === false ? 'Enable' : 'Pause'}
                </button>
                <button
                  type="button"
                  onClick={() => removeOffer(offer.id)}
                  aria-label={`Remove ${offer.title}`}
                  data-testid={`offer-remove-${offer.id}`}
                  className="rounded p-1.5 text-white/45 transition hover:bg-brand-pink/15 hover:text-brand-pink"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className={`${WELL} flex flex-col gap-3`}>
        <BrandInput
          type="text"
          maxLength={MAX_OFFER_TITLE}
          placeholder="Offer title, e.g. 2-for-1 cocktails"
          data-testid="offer-title-input"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
        <BrandInput
          type="text"
          placeholder="Detail (optional) — shown when a guest opens your card"
          data-testid="offer-description-input"
          value={draft.description}
          onChange={(e) =>
            setDraft((d) => ({ ...d, description: e.target.value }))
          }
        />
        <div className="flex flex-col gap-2">
          <BrandLabel>Runs on (optional — blank means every day)</BrandLabel>
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                aria-pressed={draft.daysOfWeek.includes(day.value)}
                data-testid={`offer-day-${day.value}`}
                onClick={() => toggleDay(day.value)}
                className={`rounded-full border px-3 py-1.5 font-mono text-xs transition ${
                  draft.daysOfWeek.includes(day.value)
                    ? 'border-transparent bg-brand-warm font-bold text-white'
                    : 'border-white/20 bg-white/[0.06] text-white/70 hover:border-white/45'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <BrandLabel htmlFor="offer-valid-until">
            Runs until (optional)
          </BrandLabel>
          <BrandInput
            id="offer-valid-until"
            type="date"
            data-testid="offer-valid-until"
            value={draft.validUntil}
            onChange={(e) =>
              setDraft((d) => ({ ...d, validUntil: e.target.value }))
            }
          />
        </div>
        <button
          type="button"
          onClick={addOffer}
          disabled={!draft.title.trim()}
          data-testid="offer-add"
          className={brandButton('outline', 'sm', 'self-start')}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          Add Offer
        </button>
      </div>
    </div>
  );
}

const BORDER_STYLES = [
  {
    key: 'default',
    name: 'Default',
    description: 'Clean dark card',
    swatchClass: 'border-white/30',
  },
  {
    key: 'neon-glow',
    name: 'Neon Glow',
    description: 'Coral ring & glow',
    swatchClass: 'border-brand-pink shadow-[0_0_18px_rgba(231,59,90,0.55)]',
  },
  {
    key: 'gold-trim',
    name: 'Gold Trim',
    description: 'Gold ring & glow',
    swatchClass: 'border-brand-orange shadow-[0_0_18px_rgba(243,113,16,0.55)]',
  },
];

function BorderStyleGrid({ venueData, updateVenueData }) {
  return (
    <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1">
      {BORDER_STYLES.map((style) => (
        <button
          key={style.key}
          type="button"
          data-testid={`border-style-${style.key}`}
          className={`flex flex-col items-center gap-2 rounded-lg border px-4 py-4 transition ${
            venueData.cardBorderStyle === style.key
              ? 'border-brand-orange bg-white/[0.08]'
              : 'border-white/15 bg-white/[0.04] hover:border-white/35'
          }`}
          onClick={() => updateVenueData('cardBorderStyle', style.key)}
        >
          <span
            className={`h-10 w-16 rounded-lg border-2 bg-black/40 ${style.swatchClass}`}
          />
          <span className="font-mono text-sm font-bold text-white">
            {style.name}
          </span>
          <span className="font-mono text-xs text-white/55">
            {style.description}
          </span>
        </button>
      ))}
    </div>
  );
}

function Step4Operations({
  venueData,
  updateVenueData,
  customBorders,
  offersUnlocked,
}) {
  const handleSocialChange = (platform, value) => {
    updateVenueData('socialLinks', {
      ...venueData.socialLinks,
      [platform]: value,
    });
  };

  return (
    <div className="flex max-h-[60vh] flex-col gap-6 overflow-y-auto pr-2.5">
      <h1 className={stepTitleClass}>Operations &amp; Socials</h1>

      <div className="flex flex-col gap-2">
        <BrandLabel>Description:</BrandLabel>
        <BrandTextarea
          className="resize-y"
          placeholder="Detail your venue's atmosphere, dress code, and minimum spends..."
          value={venueData.description}
          onChange={(e) => updateVenueData('description', e.target.value)}
          rows={4}
        />
      </div>

      <div className="flex flex-col gap-2">
        <BrandLabel>Social Links:</BrandLabel>
        <BrandInput
          type="url"
          placeholder="Instagram URL"
          value={venueData.socialLinks.instagram}
          onChange={(e) => handleSocialChange('instagram', e.target.value)}
        />
        <BrandInput
          type="url"
          placeholder="Facebook URL"
          value={venueData.socialLinks.facebook}
          onChange={(e) => handleSocialChange('facebook', e.target.value)}
        />
        <BrandInput
          type="url"
          placeholder="TikTok URL"
          value={venueData.socialLinks.tiktok}
          onChange={(e) => handleSocialChange('tiktok', e.target.value)}
        />
      </div>

      {/* --- Discovery filters ---------------------------------------------
          Optional, and the consumer app is lenient about missing values — so
          the cost of leaving these blank is reach, not visibility. That is the
          argument the copy makes, because it is the true one. */}
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <BrandLabel>Discovery Filters:</BrandLabel>
          <p className="mt-1 font-mono text-xs text-white/50">
            Optional. Guests filter the swipe deck on these — a venue that sets
            them shows up in more searches.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <BrandLabel htmlFor="venue-dress-code">Dress Code</BrandLabel>
          <BrandSelect
            id="venue-dress-code"
            data-testid="venue-dress-code"
            value={venueData.dressCode}
            onChange={(e) => updateVenueData('dressCode', e.target.value)}
          >
            <option value="">No preference</option>
            {DRESS_CODES.map((code) => (
              <option key={code.value} value={code.value}>
                {code.label}
              </option>
            ))}
          </BrandSelect>
        </div>

        <div className="flex flex-col gap-2">
          <BrandLabel htmlFor="venue-cover-charge">
            Cover Charge (ZAR) — enter 0 for free entry
          </BrandLabel>
          <BrandInput
            id="venue-cover-charge"
            type="number"
            min="0"
            step="1"
            placeholder="Leave blank if it varies"
            data-testid="venue-cover-charge"
            value={venueData.coverCharge}
            onChange={(e) => updateVenueData('coverCharge', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <BrandLabel>Music Genres</BrandLabel>
          <ChipMultiSelect
            options={MUSIC_GENRES}
            selected={venueData.musicGenres}
            onChange={(next) => updateVenueData('musicGenres', next)}
            testIdPrefix="genre"
          />
        </div>

        <div className="flex flex-col gap-2">
          <BrandLabel>Dietary Options</BrandLabel>
          <ChipMultiSelect
            options={DIETARY_OPTIONS}
            selected={venueData.dietaryOptions}
            onChange={(next) => updateVenueData('dietaryOptions', next)}
            testIdPrefix="dietary"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <BrandLabel>Offers &amp; Promos:</BrandLabel>
        {offersUnlocked ? (
          <OffersEditor
            offers={venueData.offers}
            onChange={(next) => updateVenueData('offers', next)}
          />
        ) : (
          <FeatureLocked
            variant="compact"
            requiredTier="pro"
            featureName="Venue Offers"
          >
            <OffersEditor offers={venueData.offers} onChange={() => {}} />
          </FeatureLocked>
        )}
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <BrandLabel>Premium Card Styling:</BrandLabel>
        {customBorders ? (
          <BorderStyleGrid
            venueData={venueData}
            updateVenueData={updateVenueData}
          />
        ) : (
          <FeatureLocked
            variant="compact"
            requiredTier="pro"
            featureName="Premium Card Styling"
          >
            <BorderStyleGrid
              venueData={venueData}
              updateVenueData={updateVenueData}
            />
          </FeatureLocked>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <BrandLabel>Hours (Configure in Dashboard Settings later):</BrandLabel>
        <p className="font-mono text-sm text-white/55">
          Detailed daily hours can be configured post-launch.
        </p>
      </div>
    </div>
  );
}

export default CreateVenue;
